import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import ApiClient from "../../methods/api/apiClient";
import environment from "../../environment";
import loader from "../../methods/loader";
import Layout2 from "../../components/global/layout2";
import dateFormat from "dateformat";

/**
 * Admin PBL Bids page — list lanes, import Excel, expand a row to see
 * carrier bids, award a bid, edit the bid deadline.
 */
const PblBids = () => {
  const history = useHistory();
  const [lanes, setLanes] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ page: 1, count: 25, search: "", status: "" });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [expandedDetail, setExpandedDetail] = useState(null);
  const [selected, setSelected] = useState({}); // { [laneId]: true }

  const fetchList = async (override = {}) => {
    setBusy(true);
    const p = { ...filters, ...override };
    const res = await ApiClient.get("pbl/lanes", p);
    setBusy(false);
    if (res && res.success) {
      setLanes(res.data || []);
      setTotal(res.total || 0);
    } else {
      toast.error((res && res.error && res.error.message) || "Failed to load lanes");
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line */ }, []);

  const onImportFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;

    // Date+time picker via Swal — required, must be in the future
    const minLocal = new Date(Date.now() + 60 * 1000).toISOString().slice(0, 16); // now+1m
    const defaultLocal = new Date(Date.now() + 7 * 24 * 3600 * 1000).toISOString().slice(0, 16); // +7d
    const { value: dueLocal, isConfirmed } = await Swal.fire({
      title: "When are bids due?",
      html:
        '<div style="text-align:left;font-size:14px;line-height:1.5">' +
        `<b>File:</b> ${file.name}<br/>` +
        `<b>Module:</b> PBL Bids<br/>` +
        `<b>Bidding window:</b> from upload time until the deadline you set below.<br/><br/>` +
        `Carriers can submit linehaul OR all-in bids on each lane until this deadline. After the deadline, lanes auto-close.` +
        '</div>',
      input: "datetime-local",
      inputAttributes: { min: minLocal },
      inputValue: defaultLocal,
      showCancelButton: true,
      confirmButtonText: "Upload & Import",
      cancelButtonText: "Cancel",
      inputValidator: (v) => {
        if (!v) return "Pick a date/time for the bid deadline";
        if (new Date(v).getTime() <= Date.now()) return "Deadline must be in the future";
      },
    });
    if (!isConfirmed) return;

    loader(true);
    const iso = new Date(dueLocal).toISOString();
    const res = await ApiClient.postFormData(
      `pbl/import?bids_due_at=${encodeURIComponent(iso)}`,
      { file }
    );
    loader(false);
    if (res && res.success) {
      const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
      const fmt = (d) => d ? new Date(d).toISOString().slice(0, 16).replace("T", " ") : "—";

      const importedList = (res.imported_lanes && res.imported_lanes.length)
        ? `<details open style="margin-top:12px">
             <summary style="cursor:pointer"><b style="color:#198754">Imported (${res.imported_lanes.length})</b></summary>
             <div style="font-size:12px;color:#6c757d;margin:4px 0">Lane codes are formatted as <span style="font-family:monospace">&lt;Line of Travel&gt;#&lt;Index&gt;</span> — the Index is from column A of your Excel, or the row number if that column is blank.</div>
             <ul style="text-align:left;margin:6px 0 0 0;padding-left:18px;max-height:160px;overflow:auto;font-family:monospace;font-size:13px">
               ${res.imported_lanes.map(c => `<li>${esc(c)}</li>`).join("")}
             </ul>
           </details>`
        : "";

      const skippedList = (res.skipped_lanes && res.skipped_lanes.length)
        ? `<details open style="margin-top:12px">
             <summary style="cursor:pointer"><b style="color:#fd7e14">Skipped — already exist (${res.skipped_lanes.length})</b></summary>
             <table style="width:100%;margin-top:6px;font-size:13px;border-collapse:collapse">
               <thead>
                 <tr style="background:#f7f9fb">
                   <th style="text-align:left;padding:4px 6px;border-bottom:1px solid #e3e6ea">Lane</th>
                   <th style="text-align:left;padding:4px 6px;border-bottom:1px solid #e3e6ea">First imported</th>
                   <th style="text-align:left;padding:4px 6px;border-bottom:1px solid #e3e6ea">Existing deadline</th>
                   <th style="text-align:left;padding:4px 6px;border-bottom:1px solid #e3e6ea">From file</th>
                 </tr>
               </thead>
               <tbody>
                 ${res.skipped_lanes.map(s => `
                   <tr>
                     <td style="padding:4px 6px;font-family:monospace"><b>${esc(s.lane_code)}</b></td>
                     <td style="padding:4px 6px">${fmt(s.createdAt)}</td>
                     <td style="padding:4px 6px">${fmt(s.bids_due_at)}</td>
                     <td style="padding:4px 6px;color:#6c757d;max-width:240px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" title="${esc(s.imported_filename || "")}">${esc(s.imported_filename || "—")}</td>
                   </tr>`).join("")}
               </tbody>
             </table>
             <div style="font-size:12px;color:#6c757d;margin-top:6px">To re-import a lane, delete the existing one first (bulk delete works).</div>
           </details>`
        : "";

      const errs = (res.errors && res.errors.length)
        ? `<details open style="margin-top:12px">
             <summary style="cursor:pointer"><b style="color:#dc3545">Errors (${res.errors.length})</b></summary>
             <ul style="text-align:left;margin:6px 0 0 0;padding-left:18px;font-size:13px">
               ${res.errors.map(er => `<li><b>${esc(er.lane_code)}</b>: ${esc(er.message)}</li>`).join("")}
             </ul>
           </details>`
        : "";

      Swal.fire({
        title: "Import complete",
        icon: (res.errors && res.errors.length) ? "warning" : "success",
        width: 720,
        html:
          `<div style="text-align:left">` +
          `Total rows: <b>${res.totalRecords}</b> &nbsp;·&nbsp; ` +
          `Imported: <b style="color:#198754">${res.imported}</b> &nbsp;·&nbsp; ` +
          `Skipped: <b style="color:#fd7e14">${res.existed}</b>` +
          importedList +
          skippedList +
          errs +
          `</div>`,
      });
      fetchList({ page: 1 });
    } else {
      Swal.fire({
        title: "Import failed",
        icon: "error",
        text: (res && res.error && res.error.message) || "Unknown error",
      });
    }
  };

  const toggleExpand = async (laneId) => {
    if (expanded === laneId) {
      setExpanded(null);
      setExpandedDetail(null);
      return;
    }
    setExpanded(laneId);
    setExpandedDetail(null);
    const res = await ApiClient.get(`pbl/lanes/${laneId}`);
    if (res && res.success) setExpandedDetail(res.data);
    else toast.error("Failed to load bids");
  };

  const award = async (bidId, laneCode) => {
    const c = await Swal.fire({
      title: `Award this bid?`,
      text: `Lane ${laneCode} will be marked AWARDED and all other bids on it will be REJECTED. This is irreversible from the UI.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, award",
    });
    if (!c.isConfirmed) return;
    loader(true);
    const res = await ApiClient.post(`pbl/bid/${bidId}/award`, {});
    loader(false);
    if (res && res.success) {
      toast.success("Bid awarded");
      toggleExpand(expanded); // refresh detail
      toggleExpand(expanded);
      fetchList();
    } else {
      toast.error((res && res.error && res.error.message) || "Award failed");
    }
  };

  const editDeadline = async (lane) => {
    const cur = lane.bids_due_at ? new Date(lane.bids_due_at).toISOString().slice(0, 16) : "";
    const { value, isConfirmed } = await Swal.fire({
      title: `Edit bid deadline — ${lane.lane_code}`,
      input: "datetime-local",
      inputValue: cur,
      showCancelButton: true,
      confirmButtonText: "Save",
    });
    if (!isConfirmed || !value) return;
    loader(true);
    const res = await ApiClient.put(`pbl/lanes/${lane.id}`, { bids_due_at: new Date(value).toISOString() });
    loader(false);
    if (res && res.success) {
      toast.success("Deadline updated");
      fetchList();
    } else toast.error((res && res.error && res.error.message) || "Update failed");
  };

  const remove = async (lane) => {
    const c = await Swal.fire({
      title: `Delete lane ${lane.lane_code}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!c.isConfirmed) return;
    loader(true);
    const res = await ApiClient.delete(`pbl/lanes/${lane.id}`);
    loader(false);
    if (res && res.success) {
      toast.success("Deleted");
      fetchList();
    } else toast.error("Delete failed");
  };

  // ---- bulk selection / delete ----
  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const allOnPageSelected = lanes.length > 0 && lanes.every((l) => selected[l.id]);

  const toggleOne = (id) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      const next = { ...selected };
      lanes.forEach((l) => { delete next[l.id]; });
      setSelected(next);
    } else {
      const next = { ...selected };
      lanes.forEach((l) => { next[l.id] = true; });
      setSelected(next);
    }
  };
  const clearSelection = () => setSelected({});

  const bulkDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    const c = await Swal.fire({
      title: `Delete ${selectedIds.length} lane${selectedIds.length === 1 ? "" : "s"}?`,
      text: "All carrier bids attached to these lanes will also be removed. This cannot be undone from the UI.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, delete ${selectedIds.length}`,
      confirmButtonColor: "#d33",
    });
    if (!c.isConfirmed) return;
    loader(true);
    const res = await ApiClient.post("pbl/lanes/bulk-delete", { ids: selectedIds });
    loader(false);
    if (res && res.success) {
      toast.success(`Deleted ${res.deleted} lane${res.deleted === 1 ? "" : "s"}${res.bids_deleted ? ` (+ ${res.bids_deleted} bid${res.bids_deleted === 1 ? "" : "s"})` : ""}`);
      clearSelection();
      fetchList();
    } else {
      toast.error((res && res.error && res.error.message) || "Bulk delete failed");
    }
  };

  const bulkDeleteAll = async () => {
    const c = await Swal.fire({
      title: `Delete ALL ${total} lane${total === 1 ? "" : "s"}?`,
      html: `Filters in effect: <b>status=${filters.status || "any"}</b>, <b>search=${filters.search || "(none)"}</b><br/><br/>This will soft-delete every matching lane and every bid on them. Cannot be undone from the UI.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, delete ALL ${total}`,
      confirmButtonColor: "#d33",
      input: "text",
      inputPlaceholder: 'Type "DELETE" to confirm',
      inputValidator: (v) => (v !== "DELETE" ? 'Type DELETE to confirm' : undefined),
    });
    if (!c.isConfirmed) return;
    loader(true);
    const qs = new URLSearchParams();
    if (filters.status) qs.set("status", filters.status);
    if (filters.search) qs.set("search", filters.search);
    const res = await ApiClient.post(`pbl/lanes/bulk-delete${qs.toString() ? "?" + qs.toString() : ""}`, { all: true });
    loader(false);
    if (res && res.success) {
      toast.success(`Deleted ${res.deleted} lane${res.deleted === 1 ? "" : "s"}${res.bids_deleted ? ` (+ ${res.bids_deleted} bid${res.bids_deleted === 1 ? "" : "s"})` : ""}`);
      clearSelection();
      fetchList();
    } else {
      toast.error((res && res.error && res.error.message) || "Bulk delete failed");
    }
  };

  const fmtDateTime = (v) => v ? dateFormat(new Date(v), "yyyy-mm-dd HH:MM") : "—";
  const timeLeft = (v) => {
    if (!v) return "—";
    const ms = new Date(v).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const mins = Math.floor((ms % 3600000) / 60000);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const statusBadge = (s) => {
    const styles = {
      open: { bg: "#e7f6ec", color: "#1c7c3d", border: "#bce5c9" },
      awarded: { bg: "#e7ecfb", color: "#1a3aa6", border: "#c1cef0" },
      closed: { bg: "#f1f3f5", color: "#6c757d", border: "#dee2e6" },
    };
    const c = styles[s] || styles.closed;
    return (
      <span
        className="text-capitalize"
        style={{
          background: c.bg, color: c.color, border: `1px solid ${c.border}`,
          fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 12,
        }}
      >
        {s}
      </span>
    );
  };

  return (
    <Layout2 title="PBL Bids" Sidetitle="Loads" searchShow="true">
      <div className="mainareas new-table-set">

        {/* ===== Toolbar ===== */}
        <div className="common-head-tbl">
          <div className="row">
            <div className="col-md-12">
              <div className="d-flex justify-content-between wrapflex flex-set-w align-items-center">
                <div className="bids-top">
                  <h4>Results<span>{total}</span></h4>
                </div>

                <article className="d-flex gap-2 fiftyflex justify-content-end flex-wrap align-items-center">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search lane / city"
                    style={{ maxWidth: 220, height: 38 }}
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") fetchList({ search: filters.search, page: 1 }); }}
                  />
                  <select
                    className="form-select"
                    style={{ maxWidth: 180, height: 38 }}
                    value={filters.status}
                    onChange={(e) => { setFilters({ ...filters, status: e.target.value, page: 1 }); fetchList({ status: e.target.value, page: 1 }); }}
                  >
                    <option value="">All statuses</option>
                    <option value="open">Open</option>
                    <option value="closed">Closed</option>
                    <option value="awarded">Awarded</option>
                  </select>

                  <label className="btn btn-primary btn-height cursor-pointer mb-0">
                    <i className="fa fa-file-excel-o me-2"></i>
                    Import PBL Excel
                    <input
                      type="file"
                      className="hidden"
                      accept=".xlsx,.xlsm"
                      onChange={onImportFile}
                    />
                  </label>
                </article>
              </div>
            </div>
          </div>
        </div>

        {/* ===== Bulk-action bar ===== */}
        {(selectedIds.length > 0 || total > 0) && (
          <div
            className="d-flex justify-content-between align-items-center mt-3 px-3 py-2"
            style={{
              background: selectedIds.length > 0 ? "#fff8e1" : "#f8f9fb",
              border: "1px solid " + (selectedIds.length > 0 ? "#ffe082" : "#e6e8eb"),
              borderRadius: 8,
            }}
          >
            <div>
              {selectedIds.length > 0 ? (
                <>
                  <b>{selectedIds.length}</b> selected
                  <button className="btn btn-link btn-sm ms-2 p-0" onClick={clearSelection}>Clear</button>
                </>
              ) : (
                <span className="text-muted small">Tick rows to bulk-delete, or use “Delete All” to clear current filter.</span>
              )}
            </div>
            <div className="d-flex gap-2">
              {selectedIds.length > 0 && (
                <button className="btn btn-danger btn-sm" onClick={bulkDeleteSelected}>
                  <i className="fa fa-trash me-1"></i> Delete Selected ({selectedIds.length})
                </button>
              )}
              {total > 0 && (
                <button className="btn btn-outline-danger btn-sm" onClick={bulkDeleteAll}>
                  Delete All ({total})
                </button>
              )}
            </div>
          </div>
        )}

        {/* ===== Table ===== */}
        <div className="table-responsive table_section mt-3 new-table">
          <table className="table">
            <thead className="table_head">
              <tr>
                <th scope="col" className="table_data" style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allOnPageSelected}
                    onChange={toggleAllOnPage}
                    title="Select all on this page"
                  />
                </th>
                <th scope="col" className="table_data" style={{ width: 36 }}></th>
                <th scope="col" className="table_data">Lane</th>
                <th scope="col" className="table_data">Origin → Destination</th>
                <th scope="col" className="table_data">Frequency</th>
                <th scope="col" className="table_data">Trip Start</th>
                <th scope="col" className="table_data">Bids Due</th>
                <th scope="col" className="table_data">Time Left</th>
                <th scope="col" className="table_data">Status</th>
                <th scope="col" className="table_data text-end">Bids</th>
                <th scope="col" className="table_data text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {busy && (
                <tr><td colSpan={11} className="text-center py-4"><i className="fa fa-spinner fa-spin fa-2x text-muted"></i></td></tr>
              )}
              {!busy && lanes.length === 0 && (
                <tr>
                  <td colSpan={11} className="text-center py-5 text-muted">
                    <div style={{ fontSize: 40, opacity: 0.4, marginBottom: 8 }}>
                      <i className="fa fa-inbox"></i>
                    </div>
                    No PBL lanes yet. Click <b>Import PBL Excel</b> to add some.
                  </td>
                </tr>
              )}
              {!busy && lanes.map((l) => (
                <React.Fragment key={l.id}>
                  <tr
                    className="data_row"
                    style={{
                      background: selected[l.id] ? "#fff9e1" : undefined,
                      borderLeft: selected[l.id] ? "3px solid #f5a623" : "3px solid transparent",
                    }}
                  >
                    <td className="table_dats" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={!!selected[l.id]}
                        onChange={() => toggleOne(l.id)}
                      />
                    </td>
                    <td className="table_dats">
                      <button
                        className="btn btn-sm"
                        title={expanded === l.id ? "Hide" : "Expand"}
                        onClick={() => toggleExpand(l.id)}
                        style={{
                          border: "1px solid #e3e6ea",
                          background: expanded === l.id ? "#eef3ff" : "white",
                          width: 30, height: 30, padding: 0, borderRadius: 6,
                        }}
                      >
                        <i className={`fa fa-chevron-${expanded === l.id ? "up" : "down"}`} style={{ fontSize: 11, color: "#5a6b7b" }}></i>
                      </button>
                    </td>
                    <td className="table_dats">
                      <div className="d-flex align-items-center gap-2">
                        <b style={{ fontSize: 14 }}>{l.line_of_travel || l.lane_code}</b>
                        {l.excel_index && (
                          <span
                            title="Excel Index column (or row #)"
                            style={{
                              background: "#1f2937", color: "white", fontFamily: "monospace",
                              fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                            }}
                          >
                            #{l.excel_index}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="table_dats">
                      <div style={{ fontWeight: 500 }}>{l.origin_city || "?"}, {l.origin_state || "?"}</div>
                      <div className="text-muted small">
                        <i className="fa fa-arrow-right me-1" style={{ fontSize: 10 }}></i>
                        {l.destination_city || "?"}, {l.destination_state || "?"}
                      </div>
                    </td>
                    <td className="table_dats" style={{ maxWidth: 320 }}>
                      <div style={{ fontWeight: 600, color: "#1f2937" }}>{l.frequency || "—"}</div>
                      <div className="small text-muted" style={{ whiteSpace: "normal", lineHeight: 1.35 }}>
                        {l.frequency_description || "—"}
                      </div>
                    </td>
                    <td className="table_dats">{l.trip_start_date ? dateFormat(new Date(l.trip_start_date), "mmm dd, yyyy") : "—"}</td>
                    <td className="table_dats">
                      <div>{l.bids_due_at ? dateFormat(new Date(l.bids_due_at), "mmm dd, yyyy") : "—"}</div>
                      <div className="text-muted small">{l.bids_due_at ? dateFormat(new Date(l.bids_due_at), "HH:MM") : ""}</div>
                    </td>
                    <td className="table_dats">
                      <span style={{ fontWeight: 600, color: timeLeft(l.bids_due_at) === "expired" ? "#dc3545" : "#1c7c3d" }}>
                        {timeLeft(l.bids_due_at)}
                      </span>
                    </td>
                    <td className="table_dats">{statusBadge(l.status)}</td>
                    <td className="table_dats text-end">
                      <span
                        className="bid-count"
                        style={{
                          display: "inline-block", minWidth: 30, padding: "3px 10px",
                          background: (l.bid_count || 0) > 0 ? "#0d6efd" : "#e9ecef",
                          color: (l.bid_count || 0) > 0 ? "white" : "#6c757d",
                          borderRadius: 12, fontSize: 12, fontWeight: 600,
                        }}
                      >
                        {l.bid_count || 0}
                      </span>
                    </td>
                    <td className="table_dats text-end">
                      <button className="btn btn-sm me-1" onClick={() => editDeadline(l)} title="Edit bid deadline"
                        style={{ border: "1px solid #c9d2dc", background: "white", width: 30, height: 30, padding: 0, borderRadius: 6 }}>
                        <i className="fa fa-clock-o" style={{ fontSize: 12, color: "#0d6efd" }}></i>
                      </button>
                      <button className="btn btn-sm" onClick={() => remove(l)} title="Delete lane"
                        style={{ border: "1px solid #f5c2c7", background: "white", width: 30, height: 30, padding: 0, borderRadius: 6 }}>
                        <i className="fa fa-trash" style={{ fontSize: 12, color: "#dc3545" }}></i>
                      </button>
                    </td>
                  </tr>

                  {expanded === l.id && (
                    <tr className="data_row">
                      <td colSpan={11} style={{ background: "#f8f9fb", padding: 0, borderTop: "none" }}>
                        {!expandedDetail ? (
                          <div className="text-center py-4"><i className="fa fa-spinner fa-spin fa-2x text-muted"></i></div>
                        ) : (
                          <div style={{ padding: "16px 24px" }}>

                            {/* Lane summary banner */}
                            <div
                              className="mb-3 d-flex align-items-center flex-wrap"
                              style={{
                                background: "white", borderRadius: 8, padding: "12px 16px",
                                border: "1px solid #e3e6ea", borderLeft: "4px solid #0d6efd",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                              }}
                            >
                              <div className="flex-grow-1">
                                <span style={{ fontSize: 18, fontWeight: 700 }}>
                                  {expandedDetail.line_of_travel || expandedDetail.lane_code}
                                </span>
                                {expandedDetail.excel_index && (
                                  <span
                                    className="ms-2"
                                    style={{ background: "#1f2937", color: "white", fontFamily: "monospace", fontSize: 12, fontWeight: 600, padding: "3px 10px", borderRadius: 4 }}
                                  >
                                    Index #{expandedDetail.excel_index}
                                  </span>
                                )}
                                {expandedDetail.excel_row && (
                                  <span className="text-muted small ms-2">(Excel row {expandedDetail.excel_row})</span>
                                )}
                                {expandedDetail.imported_filename && (
                                  <div className="small text-muted mt-1" style={{ wordBreak: "break-all" }}>
                                    <i className="fa fa-file-excel-o me-1"></i>
                                    {expandedDetail.imported_filename}
                                  </div>
                                )}
                              </div>
                              <div>{statusBadge(expandedDetail.status)}</div>
                            </div>

                            <div className="row">
                              {/* Lane details card */}
                              <div className="col-lg-7 mb-3">
                                <div style={{ background: "white", borderRadius: 8, padding: 16, border: "1px solid #e3e6ea", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
                                  <h6 className="mb-3" style={{ color: "#5a6b7b", fontWeight: 600, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                    Lane Details
                                  </h6>
                                  <div className="row g-3 small">
                                    <div className="col-md-12">
                                      <div className="text-muted">Origin</div>
                                      <div style={{ fontWeight: 500 }}>{expandedDetail.origin_address}</div>
                                      <div className="text-muted">{expandedDetail.origin_city}, {expandedDetail.origin_state}</div>
                                    </div>
                                    {(expandedDetail.stops || []).map((s) => (
                                      <div className="col-md-12" key={s.n}>
                                        <div className="text-muted">Stop {s.n}{s.arrive ? ` · arrive ${s.arrive}` : ""}</div>
                                        <div style={{ fontWeight: 500 }}>{s.address || "—"}</div>
                                        {s.city && <div className="text-muted">{s.city}, {s.state}</div>}
                                      </div>
                                    ))}
                                    <div className="col-md-12">
                                      <div className="text-muted">Destination</div>
                                      <div style={{ fontWeight: 500 }}>{expandedDetail.destination_address}</div>
                                      <div className="text-muted">{expandedDetail.destination_city}, {expandedDetail.destination_state}</div>
                                    </div>

                                    <div className="col-md-4"><div className="text-muted">Frequency</div><div style={{ fontWeight: 500 }}>{expandedDetail.frequency || "—"}</div></div>
                                    <div className="col-md-4"><div className="text-muted">Departure</div><div style={{ fontWeight: 500 }}>{expandedDetail.departure_time || "—"}</div></div>
                                    <div className="col-md-4"><div className="text-muted">Arrive destination</div><div style={{ fontWeight: 500 }}>{expandedDetail.arrive_destination || "—"}{expandedDetail.arrive_destination_day_offset ? ` (+${expandedDetail.arrive_destination_day_offset}d)` : ""}</div></div>

                                    <div className="col-md-4"><div className="text-muted">Miles</div><div style={{ fontWeight: 500 }}>{expandedDetail.miles || "—"}</div></div>
                                    <div className="col-md-4"><div className="text-muted">Travel time</div><div style={{ fontWeight: 500 }}>{expandedDetail.travel_time_allotted || "—"}</div></div>
                                    <div className="col-md-4"><div className="text-muted">Service type</div><div style={{ fontWeight: 500 }}>{expandedDetail.service_type || "—"}</div></div>

                                    <div className="col-md-6"><div className="text-muted">Annual miles</div><div style={{ fontWeight: 500 }}>{expandedDetail.annual_miles || "—"}</div></div>
                                    <div className="col-md-6"><div className="text-muted">Fuel CPM</div><div style={{ fontWeight: 500 }}>${expandedDetail.fuel_cpm || 0}</div></div>

                                    <div className="col-md-12">
                                      <div className="text-muted">Frequency explanation</div>
                                      <div
                                        className="mt-1 p-2"
                                        style={{ background: "#eef3ff", border: "1px solid #c1cef0", borderRadius: 6, color: "#1a3aa6", fontWeight: 500 }}
                                      >
                                        <b style={{ fontFamily: "monospace", marginRight: 8 }}>{expandedDetail.frequency || "—"}</b>
                                        {expandedDetail.frequency_description || "—"}
                                      </div>
                                    </div>

                                    <div className="col-md-12">
                                      <div className="text-muted">Fuel reimbursement</div>
                                      <div
                                        className="mt-1 p-2 small"
                                        style={{ background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 6, color: "#6c4400" }}
                                      >
                                        <i className="fa fa-info-circle me-1"></i>
                                        {expandedDetail.fuel_note}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Carrier bids card */}
                              <div className="col-lg-5 mb-3">
                                <div style={{ background: "white", borderRadius: 8, padding: 16, border: "1px solid #e3e6ea", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
                                  <h6 className="mb-3 d-flex align-items-center justify-content-between" style={{ color: "#5a6b7b", fontWeight: 600, fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                    <span>Carrier Bids</span>
                                    <span style={{ background: "#0d6efd", color: "white", fontSize: 11, padding: "2px 10px", borderRadius: 12, textTransform: "none" }}>
                                      {expandedDetail.bid_count || 0}
                                    </span>
                                  </h6>
                                  {(expandedDetail.bids || []).length === 0 && (
                                    <div className="text-center py-4 text-muted">
                                      <div style={{ fontSize: 32, opacity: 0.3 }}><i className="fa fa-gavel"></i></div>
                                      <div className="small mt-2">No bids yet</div>
                                    </div>
                                  )}
                                  {(expandedDetail.bids || []).length > 0 && (
                                    <div>
                                      {expandedDetail.bids.map((b) => (
                                        <div key={b.id} className="mb-2 p-2" style={{ background: "#f8f9fb", borderRadius: 6, border: "1px solid #e6e8eb" }}>
                                          <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                              <div style={{ fontWeight: 600, fontSize: 13 }}>{b.carrier_name || b.carrier_id}</div>
                                              <div className="d-flex align-items-center mt-1 gap-2">
                                                <span
                                                  style={{
                                                    background: b.bid_type === "linehaul" ? "#cff4fc" : "#ffe9b3",
                                                    color: b.bid_type === "linehaul" ? "#055160" : "#664d00",
                                                    fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                                                  }}
                                                >
                                                  {b.bid_type === "linehaul" ? "Linehaul" : "All-In"}
                                                </span>
                                                <span style={{ fontWeight: 700, fontSize: 15, color: "#0d6efd" }}>
                                                  ${b.bid_type === "linehaul" ? b.linehaul_rate : b.all_in_rate}
                                                </span>
                                              </div>
                                              {b.notes && <div className="small text-muted mt-1" style={{ fontStyle: "italic" }}>“{b.notes}”</div>}
                                            </div>
                                            <div className="text-end">
                                              <span
                                                className="text-capitalize"
                                                style={{
                                                  background: b.status === "awarded" ? "#cfe2ff" : b.status === "rejected" ? "#f8d7da" : "#e9ecef",
                                                  color: b.status === "awarded" ? "#0a58ca" : b.status === "rejected" ? "#842029" : "#495057",
                                                  fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
                                                }}
                                              >
                                                {b.status}
                                              </span>
                                              {b.status === "submitted" && expandedDetail.status === "open" && (
                                                <button className="btn btn-success btn-sm mt-2 d-block" onClick={() => award(b.id, l.lane_code)}>
                                                  <i className="fa fa-trophy me-1"></i> Award
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout2>
  );
};

export default PblBids;
